import React, { createContext, useContext, useEffect, useState } from "react";
import { rtdb } from "utils/firebase";
import { environment } from "utils/constant";

const NotificationContext = createContext();

const getStoredOrgId = () => {
  try {
    const org = JSON.parse(localStorage.getItem("organization") || "null");
    return org?.id ?? null;
  } catch (e) {
    return null;
  }
};

const getStoredUserId = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return user?.user ?? null;
  } catch (e) {
    return null;
  }
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children, organizationId: propOrganizationId, userId: propUserId }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [newNotification, setNewNotification] = useState(null);
  const [organizationId, setOrganizationId] = useState(
    () => propOrganizationId ?? getStoredOrgId()
  );
  const [userId, setUserId] = useState(() => propUserId ?? getStoredUserId());

  // Keep in sync with props
  useEffect(() => {
    const nextOrganizationId = propOrganizationId ?? getStoredOrgId();
    setOrganizationId((currentOrganizationId) =>
      currentOrganizationId === nextOrganizationId
        ? currentOrganizationId
        : nextOrganizationId
    );
  }, [propOrganizationId]);

  useEffect(() => {
    const nextUserId = propUserId ?? getStoredUserId();
    setUserId((currentUserId) =>
      currentUserId === nextUserId ? currentUserId : nextUserId
    );
  }, [propUserId]);

  // Listen for changes in localStorage
  useEffect(() => {
    const syncFromStorage = () => {
      const nextOrganizationId = getStoredOrgId();
      const nextUserId = getStoredUserId();

      setOrganizationId((currentOrganizationId) =>
        currentOrganizationId === nextOrganizationId
          ? currentOrganizationId
          : nextOrganizationId
      );
      setUserId((currentUserId) =>
        currentUserId === nextUserId ? currentUserId : nextUserId
      );
    };

    const handleStorageChange = (e) => {
      if (!e.key || e.key === "organization" || e.key === "user") {
        syncFromStorage();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("organizationChanged", syncFromStorage);
    window.addEventListener("authChanged", syncFromStorage);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("organizationChanged", syncFromStorage);
      window.removeEventListener("authChanged", syncFromStorage);
    };
  }, []);

  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    setNewNotification(null);
    updateTabTitle(0);
  }, [organizationId, userId]);

  // Listen for real-time notifications
  useEffect(() => {
    if (!organizationId || !userId) return;

    const notificationsRef = rtdb.ref(`notifications-${environment}/${organizationId}`);

    const handleNotificationChange = (snapshot) => {
      const notificationsData = snapshot.val() || {};
      const notificationsList = Object.entries(notificationsData)
        .map(([key, value]) => ({ id: key, ...value }))
        .filter((n) => n.target_ids && n.target_ids.includes(userId) && n.actor_id !== userId)
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      setNotifications(notificationsList);

      const unread = notificationsList.filter((n) => !(n.read_by || []).includes(userId)).length;
      setUnreadCount(unread);

      // Update browser tab title
      updateTabTitle(unread);
    };

    // Listen for new notifications
    const handleChildAdded = (snapshot) => {
      const notification = snapshot.val();
      if (notification) {
        const notificationWithId = { id: snapshot.key, ...notification };
        const actor_id = notification.actor_id || null;
        const target_ids = notification.target_ids || [];
        const isTarget = target_ids.includes(userId);
        const isNotActor = actor_id !== userId;
        const isUnread = !(notification.read_by || []).includes(userId);

        if (isTarget && isNotActor && isUnread) {
          setNewNotification(notificationWithId);

          // Show browser notification if permission granted
          showBrowserNotification(notificationWithId);

          // Play notification sound
          // playNotificationSound();
        }
      }
    };

    notificationsRef.on("value", handleNotificationChange);

    notificationsRef.on("child_added", handleChildAdded);

    return () => {
      notificationsRef.off("value", handleNotificationChange);
      notificationsRef.off("child_added", handleChildAdded);
    };
  }, [organizationId, userId]); // Added userId to dependencies

  const updateTabTitle = (count) => {
    const baseTitle = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;

    // Update favicon with badge
    // updateFavicon(count);
  };

  const updateFavicon = (count) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    canvas.width = 32;
    canvas.height = 32;

    img.onload = () => {
      // Draw the original favicon
      ctx.drawImage(img, 0, 0, 32, 32);

      if (count > 0) {
        // Draw notification badge with larger size
        ctx.fillStyle = "#FF0000"; // Brighter red
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, 2 * Math.PI); // Increased radius from 8 to 10
        ctx.fill();

        // Add a white border for better visibility
        ctx.strokeStyle = "white";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw count text with improved visibility
        ctx.fillStyle = "white";
        ctx.font = "bold 11px Arial"; // Increased font size
        ctx.textAlign = "center";
        ctx.textBaseline = "middle"; // Better text alignment
        ctx.fillText(count > 99 ? "99+" : count.toString(), 24, 8);
      }

      // Update favicon
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = canvas.toDataURL();
    };

    // Load current favicon or use default
    img.src =
      document.querySelector("link[rel*='icon']")?.href || "/download.svg";
  };

  useEffect(() => {
    const unread = notifications.filter((n) => !(n.read_by || []).includes(userId)).length;
    setUnreadCount(unread);
    updateTabTitle(unread);
  }, [notifications, userId]);

  const showBrowserNotification = (notification) => {
    if ("Notification" in window && Notification.permission === "granted") {
      new Notification(notification.title, {
        body: notification.message,
        icon: "/download.svg",
        tag: notification.id, // Prevents duplicate notifications
      });
    }
  };

  const playNotificationSound = () => {
    // You can add a notification sound file to your public folder
    const audio = new Audio("/web-notification.wav");
    audio.volume = 0.3;
    audio.play().catch(() => {
      // Ignore errors if sound can't play
    });
  };

  const markAsRead = (notificationId) => {
    if (!organizationId || !userId) return;
    const notification = notifications.find(n => n.id === notificationId);
    if (!notification) return;

    const readBy = notification.read_by || [];
    if (!readBy.includes(userId)) {
      const notificationsRef = rtdb.ref(
        `notifications-${environment}/${organizationId}`
      );
      notificationsRef.child(notificationId).update({
        read_by: [...readBy, userId]
      });
    }
  };

  const markAllAsRead = () => {
    if (!organizationId || !userId) return;
    const notificationsRef = rtdb.ref(
      `notifications-${environment}/${organizationId}`
    );
    notifications
      .filter((n) => !(n.read_by || []).includes(userId))
      .forEach((notification) => {
        const readBy = notification.read_by || [];
        notificationsRef.child(notification.id).update({
          read_by: [...readBy, userId]
        });
      });
  };

  const dismissNewNotification = () => {
    setNewNotification(null);
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window && Notification.permission === "default") {
      const permission = await Notification.requestPermission();
      return permission === "granted";
    }
    return Notification.permission === "granted";
  };

  const value = {
    notifications,
    unreadCount,
    newNotification,
    markAsRead,
    markAllAsRead,
    dismissNewNotification,
    requestNotificationPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
